import { useQuery } from '@apollo/client';
import { Ellipsis, Trash } from '@clearkit/icons';
import {
  CKActionMenu,
  CKButton,
  CKLayoutHorizontal,
  CKPopover,
  CKSelectNext,
} from 'clearkit';

import { ButtonContainer } from '~/components/unified/ButtonContainer';
import apollo from '~/lib/apollo';

import CreateTargetMarketQuery from '../queries/CreateTargetMarket.graphql';
import DestroyTargetMarketQuery from '../queries/DestroyTargetMarket.graphql';
import TargetMarketsQuery from '../queries/TargetMarkets.graphql';
import UpdateTargetMarketQuery from '../queries/UpdateTargetMarket.graphql';
import { TargetMarketForm } from './Form';
import { TargetMarketSidebar } from './TargetMarketSidebar';

export const createTargetMarket = async ({ name, search }: any) => {
  const { data, errors } = await apollo.mutate({
    mutation: CreateTargetMarketQuery,
    variables: {
      input: { name, search },
    },
  });

  if (errors?.length) {
    throw new Error('Error creating target market');
  }

  return data;
};

export const updateTargetMarket = async ({ id, search }: any) => {
  const { data, errors } = await apollo.mutate({
    mutation: UpdateTargetMarketQuery,
    variables: { input: { id, search } },
  });

  if (errors?.length) {
    throw new Error('Error updating target market');
  }

  return data;
};

export const destroyTargetMarket = async ({ id }: any) => {
  const { data, errors } = await apollo.mutate({
    mutation: DestroyTargetMarketQuery,
    variables: { input: { id } },
  });

  if (errors?.length) {
    throw new Error('Error destroying target market');
  }

  return data;
};

const Sidebar = ({ search, id, setTargetMarket, setSearch, dirty }: any) => {
  const { data, loading, refetch } = useQuery(TargetMarketsQuery);
  const targetMarkets = data?.targetMarkets?.targetMarkets || [];

  const findTargetMarket = (id: string) =>
    targetMarkets.find((tm: any) => tm.id === id);

  const handleDiscardChanges = () => {
    if (id) {
      const targetMarket = findTargetMarket(id);
      setTargetMarket({ ...targetMarket });
    } else {
      setSearch({});
    }
  };

  const handleChangeTargetMarket = (id: string) => {
    const targetMarket = findTargetMarket(id);
    setTargetMarket({ ...targetMarket });
  };

  const handleUpsertTargetMarket = async () => {
    if (id) {
      await updateTargetMarket({ id, search });
    } else {
      await createTargetMarket({
        name: 'Target Market #' + (targetMarkets.length + 1),
        search,
      });
      // TODO: Handle redirecting to new target market
    }
    refetch();
  };

  const handleDeleteTargetMarket = async () => {
    await destroyTargetMarket({ id });
    setTargetMarket({ id: null, search: {} });

    refetch();
  };

  const selectedTargetMarket = id ? findTargetMarket(id) : {};

  return (
    <TargetMarketSidebar title="Filters">
      <TargetMarketForm search={search || {}} setSearch={setSearch} />
      <TargetMarketSidebar.Footer>
        <CKLayoutHorizontal>
          <ButtonContainer>
            <CKButton
              isDisabled={!dirty}
              onClick={handleUpsertTargetMarket}
              variant="bold"
              variantColor="blue"
            >
              Save List...
            </CKButton>

            <CKButton isDisabled={!dirty} onClick={handleDiscardChanges}>
              Discard
            </CKButton>
          </ButtonContainer>
          {id ? (
            <CKPopover
              popover={
                <CKActionMenu>
                  <CKActionMenu.Item
                    icon={<Trash />}
                    isDestructive
                    onClick={handleDeleteTargetMarket}
                  >
                    Delete list
                  </CKActionMenu.Item>
                </CKActionMenu>
              }
              popoverPlacement="top-end"
            >
              <CKButton>
                <Ellipsis />
              </CKButton>
            </CKPopover>
          ) : null}
        </CKLayoutHorizontal>
        <CKSelectNext
          isDisabled={loading || targetMarkets.length === 0}
          items={targetMarkets.map((targetMarket: any) => ({
            label: targetMarket.name,
            value: targetMarket.id,
          }))}
          onChange={(item) => {
            handleChangeTargetMarket(item.value);
          }}
          placeholder="Select a list..."
          value={{
            label: selectedTargetMarket.name,
            value: selectedTargetMarket.id,
          }}
        />
      </TargetMarketSidebar.Footer>
    </TargetMarketSidebar>
  );
};

export { Sidebar };
